import {gsap} from "gsap/all";

// ===========================================================================
// DEBUG PANEL — never shown unless the page is loaded with ?dustDebug.
// Sliders over the landing dust: how much is blown, how it rolls out, its size
// and depth, and the colors it is made of. Self-activating, js/index.js picks
// every *Debug.js up on its own. It reads the effect off window.app.dustCloud,
// so no production code references it.
//
// "Show ground" puts the landed ground and its shadow on screen from wherever
// the app is, so the dust has somewhere to come from without playing the run
// to reach it. "Blow dust" and "Stop dust" drive the strength by hand, which
// is what the landing does on its own. "Pause game" freezes everything but the
// dust.
//
// The readout at the bottom is the options block to paste back into
// DustCloud, or to hand to its constructor in EDL.initGame.
// ===========================================================================

// How much dust is blown and how long a puff lives.
const EMIT_PARAMS = [
  ["emitPerSecond", 10, 300, 5],
  ["lifeMin", 0.2, 5, 0.1],
  ["lifeMax", 0.2, 8, 0.1],
  ["spawnWidth", 0, 1600, 10],
  ["originOffsetX", -200, 200, 1],
  ["originOffsetY", -200, 200, 1]
];

// How a puff rolls out, in screen pixels.
const MOTION_PARAMS = [
  ["speedMin", 0, 1500, 10],
  ["speedMax", 0, 2000, 10],
  ["drag", 0, 5, 0.05],
  ["rise", 0, 400, 5]
];

// What a puff looks like, and how the cloud splits around the ship.
const LOOK_PARAMS = [
  ["sizeStart", 10, 600, 5],
  ["sizeEnd", 10, 1200, 5],
  ["alphaMin", 0, 1, 0.01],
  ["alphaMax", 0, 1, 0.01],
  ["depthY", 0, 400, 5],
  ["frontShare", 0, 1, 0.01],
  ["frontAlpha", 0, 1, 0.01],
  ["strength", 0, 1, 0.01]
];

function getDust() {
  return window.app && window.app.dustCloud;
}

// A color only shows once the sprites are stamped again, they are tinted when
// they are built rather than at draw time. Puffs already out keep theirs.
function writeColor(index, color) {
  const dust = getDust();

  dust.palette = dust.palette.slice();
  dust.palette[index] = color;
  dust.sprites = dust.buildSprites();
}

// The ground where the landing leaves it, with its shadow, from wherever the
// app is.
function showGround(app) {
  const launch = () => {
    app.killTimeline(app.currentTimeline);
    app.game.stop();

    gsap.set(app.getMarsGround(), {
      x: 0,
      y: 0,
      autoAlpha: 1
    });
    gsap.set(app.getMarsGroundShadow(), {
      x: 0,
      y: 0,
      scale: 1,
      autoAlpha: 1
    });
  };

  if (parseInt(app.currentScreen) === 2) launch();
  else app.goToScreen(2, launch);
}

// The page styles every button with the display font size and leaves its color
// to the browser, which on this panel is black text on a black ground at forty
// odd pixels. So these say everything themselves.
function makeButton(label, onClick) {
  const button = document.createElement("button");

  button.textContent = label;
  button.style.cssText =
    "display:block;width:100%;margin:8px 0 0;padding:6px;border:0;border-radius:4px;" +
    "font:bold 12px/1.4 monospace;color:#000;background:#3EF0FF;cursor:pointer";
  button.addEventListener("click", onClick);

  return button;
}

function buildPanel() {
  const dust = getDust();

  const panel = document.createElement("div");
  panel.style.cssText =
    "position:fixed;top:12px;right:12px;z-index:99999;background:rgba(0,0,0,.8);" +
    "color:#fff;font:12px/1.4 monospace;padding:12px 14px;border-radius:8px;width:330px;" +
    "max-height:calc(100vh - 24px);overflow:auto";

  const readout = document.createElement("pre");
  readout.style.cssText = "margin:10px 0 0;white-space:pre-wrap;color:#3EF0FF;font-size:11px";

  const keys = [...EMIT_PARAMS, ...MOTION_PARAMS, ...LOOK_PARAMS]
    .map(([key]) => key)
    .filter((key) => key !== "strength");

  const refresh = () => {
    const colors = dust.palette.map((color) => `"${color}"`).join(", ");

    readout.textContent =
      keys.map((key) => `${key}: ${dust[key]},`).join("\n") +
      `\npalette: [${colors}]`;
  };

  const show = makeButton("Show ground", () => showGround(window.app));
  const blow = makeButton("Blow dust", () => dust.setStrength(1, 0.5));
  const stop = makeButton("Stop dust", () => dust.setStrength(0, 0.5));

  // The dust is left running while everything else is held, otherwise the
  // sliders would have nothing moving to show their effect on.
  const pause = makeButton("Pause game", () => {
    const app = window.app;

    if (app.isGameplayPaused()) {
      app.resumeGameplay();
      pause.textContent = "Pause game";
      return;
    }

    app.pauseGameplay();
    dust.setPaused(false);
    pause.textContent = "Resume game";
  });

  panel.append(show, blow, stop, pause);

  const addSliders = (params, title) => {
    const heading = document.createElement("div");
    heading.style.cssText = "margin:10px 0 4px;font-weight:bold;color:#FF3EA5";
    heading.textContent = title;
    panel.append(heading);

    params.forEach(([key, min, max, step]) => {
      const row = document.createElement("label");
      row.style.cssText = "display:flex;align-items:center;gap:8px";

      const name = document.createElement("span");
      name.style.cssText = "flex:0 0 104px;font-size:11px;overflow:hidden;text-overflow:ellipsis";
      name.textContent = key;

      const slider = document.createElement("input");
      slider.type = "range";
      slider.min = min;
      slider.max = max;
      slider.step = step;
      slider.value = dust[key];
      slider.style.flex = "1";

      const decimals = step < 0.01 ? 3 : (step < 0.1 ? 2 : (step < 1 ? 1 : 0));

      const val = document.createElement("span");
      val.style.cssText = "flex:0 0 46px;font-size:11px;text-align:right";
      val.textContent = Number(slider.value).toFixed(decimals);

      slider.addEventListener("input", () => {
        const value = parseFloat(slider.value);
        val.textContent = value.toFixed(decimals);

        // The strength goes through its own setter, which also wakes the
        // ticker up. Everything else is read on the next frame.
        if (key === "strength") dust.setStrength(value);
        else dust[key] = value;

        refresh();
      });

      row.append(name, slider, val);
      panel.append(row);
    });
  };

  addSliders(EMIT_PARAMS, "Emission");
  addSliders(MOTION_PARAMS, "Motion");
  addSliders(LOOK_PARAMS, "Look");

  // A puff picks one of these at random when it is born. Each one is a color
  // input rather than a slider, the sprites are rebuilt on every change.
  const colorHeading = document.createElement("div");
  colorHeading.style.cssText = "margin:10px 0 4px;font-weight:bold;color:#FF3EA5";
  colorHeading.textContent = "Palette";
  panel.append(colorHeading);

  dust.palette.forEach((color, index) => {
    const row = document.createElement("label");
    row.style.cssText = "display:flex;align-items:center;gap:8px;margin:2px 0";

    const name = document.createElement("span");
    name.style.cssText = "flex:0 0 104px;font-size:11px";
    name.textContent = `palette ${index}`;

    const input = document.createElement("input");
    input.type = "color";
    input.value = color;
    input.style.cssText = "flex:1;height:22px;background:none;border:0;padding:0";

    const val = document.createElement("span");
    val.style.cssText = "flex:0 0 66px;font-size:11px;text-align:right";
    val.textContent = color;

    input.addEventListener("input", () => {
      const value = input.value.toUpperCase();
      val.textContent = value;
      writeColor(index, value);
      refresh();
    });

    row.append(name, input, val);
    panel.append(row);
  });

  panel.append(readout);
  refresh();

  document.body.appendChild(panel);
}

function init() {
  if (!new URLSearchParams(window.location.search).has("dustDebug")) return;

  const tryAttach = () => {
    if (!getDust()) return setTimeout(tryAttach, 200);

    buildPanel();
  };

  tryAttach();
}

init();
